import {
  DayPartSections,
  ID,
  newTaskSuffix,
  rootId,
  SortingMode,
  Task,
  TaskListItemTypes,
  TaskListTreeItem,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { addDays, endOfDay, isAfter } from 'date-fns';

import { getSortedTasks } from '../get-sorted-tasks';
import { getDueTaskPlanning } from '../get-task-planning';
import { isNumber } from '../is-number';
import { getActiveSchedule } from '../schedules';

export type BuildDayPartTreeOptions = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
  hideNewTaskRow?: boolean;
  dayPartLabels: Record<DayPartSections, string>;
  rootLabel: string;
  newTaskLabel: string;
};

const getTaskTimeslot = (
  task: Task,
  today: Timestamp,
  plannedOccurrence:
    | {
        date: Timestamp;
        timeslot?: {
          startTime?: number | null;
          duration?: number | null;
        } | null;
      }
    | undefined,
):
  | { startTime?: number | null; duration?: number | null }
  | null
  | undefined => {
  if (plannedOccurrence?.timeslot) {
    return plannedOccurrence.timeslot;
  }

  const schedule = getActiveSchedule(task.schedules, today);
  if (schedule?.timeslot) {
    return schedule.timeslot;
  }

  return task.timeslot;
};

const getDayPartSection = (
  timeslot:
    | { startTime?: number | null; duration?: number | null }
    | null
    | undefined,
): DayPartSections => {
  if (!isNumber(timeslot?.startTime)) {
    return DayPartSections.AllDay;
  }

  const date = new Date();
  date.setHours(0, 0, 0, timeslot!.startTime!);
  const startHour = date.getHours();

  if (startHour < 12) {
    return DayPartSections.Morning;
  }

  if (startHour >= 12 && startHour < 18) {
    return DayPartSections.Afternoon;
  }

  return DayPartSections.Evening;
};

export const buildDayPartTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
  hideNewTaskRow,
  dayPartLabels,
  rootLabel,
  newTaskLabel,
}: BuildDayPartTreeOptions): Record<ID, TaskListTreeItem> => {
  const overdueDeadline = endOfDay(addDays(today, -1));
  const dayPartTreeItems: Record<ID, TaskListTreeItem> = {
    [DayPartSections.Overdue]: {
      id: DayPartSections.Overdue,
      type: TaskListItemTypes.DayPart,
      name: dayPartLabels[DayPartSections.Overdue],
      children: [],
      isFolder: true,
    },
    [DayPartSections.AllDay]: {
      id: DayPartSections.AllDay,
      type: TaskListItemTypes.DayPart,
      name: dayPartLabels[DayPartSections.AllDay],
      children: [],
      isFolder: true,
    },
    [DayPartSections.Morning]: {
      id: DayPartSections.Morning,
      type: TaskListItemTypes.DayPart,
      name: dayPartLabels[DayPartSections.Morning],
      children: [],
      isFolder: true,
    },
    [DayPartSections.Afternoon]: {
      id: DayPartSections.Afternoon,
      type: TaskListItemTypes.DayPart,
      name: dayPartLabels[DayPartSections.Afternoon],
      children: [],
      isFolder: true,
    },
    [DayPartSections.Evening]: {
      id: DayPartSections.Evening,
      type: TaskListItemTypes.DayPart,
      name: dayPartLabels[DayPartSections.Evening],
      children: [],
      isFolder: true,
    },
  };
  const dayPartIds = [
    DayPartSections.Overdue,
    DayPartSections.AllDay,
    DayPartSections.Morning,
    DayPartSections.Afternoon,
    DayPartSections.Evening,
  ];

  const taskDayPartMap: Record<ID, DayPartSections> = {};
  const overdueTasks: Task[] = [];
  const standardTasksMap = new Map<ID, Task>();

  tasks.forEach((task) => {
    if (task.completedAt) {
      return;
    }

    const overduePlanning = getDueTaskPlanning(task, {
      date: overdueDeadline,
      today,
      weekStartsOn,
    });

    const duePlanning = overduePlanning.filter(
      (plannedOccurrence) => !isAfter(plannedOccurrence.date, overdueDeadline),
    );

    const plannedOccurrence = duePlanning[duePlanning.length - 1];
    const isOverdue =
      !!plannedOccurrence ||
      (task.deadlineDeadline &&
        !isAfter(task.deadlineDeadline, overdueDeadline));

    if (isOverdue) {
      taskDayPartMap[task.id] = DayPartSections.Overdue;
      overdueTasks.push(task);
      return;
    }

    const planning = getDueTaskPlanning(task, {
      date: today,
      today,
      weekStartsOn,
    });

    const firstOccurrence = planning[0];
    if (!firstOccurrence) {
      const timeslot = task.timeslot;
      taskDayPartMap[task.id] = getDayPartSection(timeslot);
      standardTasksMap.set(task.id, task);
      return;
    }

    const timeslot = getTaskTimeslot(task, today, firstOccurrence);
    taskDayPartMap[task.id] = getDayPartSection(timeslot);
    standardTasksMap.set(task.id, task);
  });

  const customTaskOrder =
    sortingMode === SortingMode.Custom
      ? dayPartIds.reduce<ID[]>((acc, dayPartId) => {
          const sectionOrder = customOrder?.[dayPartId] ?? [];
          acc.push(...sectionOrder);
          return acc;
        }, [])
      : [];

  const overdueSortedTasks = getSortedTasks(overdueTasks, {
    today,
    weekStartsOn,
    order: customTaskOrder,
    mode: [SortingMode.DueDate],
    allDayFirst: true,
    invertedTimeslots: true,
  });

  overdueSortedTasks.forEach((task) => {
    const dayPartTreeItem = dayPartTreeItems[DayPartSections.Overdue];
    if (dayPartTreeItem) {
      dayPartTreeItem.children?.push(task.id);
    }

    dayPartTreeItems[task.id] = {
      id: task.id,
      name: task.name,
      type: TaskListItemTypes.Task,
    };
  });

  const standardSortedTasks = getSortedTasks(
    Array.from(standardTasksMap.values()),
    {
      today,
      weekStartsOn,
      order: customTaskOrder,
      mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
    },
  );

  const allTreeItems = standardSortedTasks.reduce<Record<ID, TaskListTreeItem>>(
    (acc, task) => {
      const dayPart = taskDayPartMap[task.id];
      if (!dayPart || dayPart === DayPartSections.Overdue) {
        return acc;
      }

      const dayPartTreeItem = acc[dayPart];
      if (dayPartTreeItem) {
        dayPartTreeItem.children?.push(task.id);
      }

      acc[task.id] = {
        id: task.id,
        name: task.name,
        type: TaskListItemTypes.Task,
      };

      return acc;
    },
    dayPartTreeItems,
  );

  if (!hideNewTaskRow) {
    dayPartIds.forEach((dayPartId) => {
      if (dayPartId === DayPartSections.Overdue) {
        return;
      }

      const dayPartTreeItem = allTreeItems[dayPartId];
      if (!dayPartTreeItem) {
        return;
      }
      const newTaskItemId = `${dayPartId}${newTaskSuffix}`;
      dayPartTreeItem.children?.push(newTaskItemId);
      allTreeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: newTaskLabel,
        type: TaskListItemTypes.NewTask,
      };
    });
  }

  const sectionIds: ID[] = dayPartIds.filter((dayPartId) => {
    const treeItem = allTreeItems[dayPartId];
    return (
      treeItem &&
      treeItem.children &&
      (showEmptyGroups || treeItem.children.length)
    );
  });

  return {
    [rootId]: {
      id: rootId,
      name: rootLabel,
      type: TaskListItemTypes.Root,
      children: sectionIds,
    },
    ...allTreeItems,
  };
};
